import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CButton } from "@coreui/react-pro";
import {
  TradingStrategySignalAction,
  type ReceivedSignalStrategyDto,
  type RelatedTradingStrategySignalDto,
} from "../../../api/hooks/useSignals";
import type { TradingStrategyDto } from "../../../api/hooks/useTradingStrategies";
import { formatSignalTimeBrief } from "../../../lib/dates";
import { StrategyInstruments } from "./StrategyInstruments";
import { StrategySideLabel } from "../../trading-strategies/components/StrategySideLabel";

// One row of tiles on a wide screen; the rest are a click away.
const SHOWN = 6;

interface StrategyStandingProps {
  strategies: ReceivedSignalStrategyDto[];
  /** What each strategy trades, by id. One missing from here shows without its instruments. */
  details: ReadonlyMap<string, TradingStrategyDto>;
  selectedId: string | null;
  /** Picking the selected strategy again clears the filter. */
  onSelect: (strategyId: string) => void;
}

/** In the market, then out of it, then no signal yet; newest signal first within each. */
function busiestFirst(strategies: ReceivedSignalStrategyDto[]): ReceivedSignalStrategyDto[] {
  const rank = (strategy: ReceivedSignalStrategyDto) =>
    strategy.latestSignal === null ? 2 : strategy.latestSignal.action === TradingStrategySignalAction.Buy ? 0 : 1;

  return [...strategies].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      (b.latestSignal?.receivedAt ?? "").localeCompare(a.latestSignal?.receivedAt ?? "") ||
      a.name.localeCompare(b.name),
  );
}

/** Where each followed strategy stands now, read from its latest signal. Picking one filters the list to it. */
export function StrategyStanding({ strategies, details, selectedId, onSelect }: StrategyStandingProps) {
  const { t } = useTranslation(["signals", "tradingStrategies"]);
  const [showAll, setShowAll] = useState(false);

  const ordered = busiestFirst(strategies);
  // A strategy picked from the filter stays visible even when it ranks below the first six.
  const openForSelection = ordered.findIndex((strategy) => strategy.id === selectedId) >= SHOWN;
  const expanded = showAll || openForSelection;
  const shown = expanded ? ordered : ordered.slice(0, SHOWN);

  return (
    <section className="mb-4" aria-labelledby="strategy-standing-title">
      <div id="strategy-standing-title" className="text-uppercase small fw-bold text-body-secondary mb-2">
        {t("feed.standingTitle")}
      </div>
      <div className="row row-cols-2 row-cols-md-3 row-cols-xl-6 g-2">
        {shown.map((strategy) => {
          const selected = strategy.id === selectedId;
          const detail = details.get(strategy.id);

          return (
            <div key={strategy.id} className="col">
              <button
                type="button"
                className={`card h-100 w-100 text-start p-2 d-flex flex-column gap-1${selected ? " border-primary" : ""}`}
                aria-pressed={selected}
                onClick={() => onSelect(strategy.id)}
              >
                <span className="fw-semibold small text-truncate w-100">{strategy.name}</span>
                <StrategySideLabel direction={detail?.direction} iconSize={14} className="small" />
                {detail && <StrategyInstruments strategy={detail} compact />}
                <Standing latest={strategy.latestSignal} />
              </button>
            </div>
          );
        })}
      </div>
      {ordered.length > SHOWN && (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2 small text-body-secondary">
          <span>{t("feed.standingShowing", { shown: shown.length, total: ordered.length })}</span>
          {!openForSelection && (
            <CButton color="link" size="sm" className="p-0" onClick={() => setShowAll(!showAll)}>
              {showAll ? t("feed.standingShowFewer") : t("feed.standingShowAll", { count: ordered.length })}
            </CButton>
          )}
        </div>
      )}
    </section>
  );
}

/** Where the strategy stands, in words; the dot only repeats it. */
function Standing({ latest }: { latest: RelatedTradingStrategySignalDto | null }) {
  const { t } = useTranslation("signals");

  const inMarket = latest?.action === TradingStrategySignalAction.Buy;
  const dot =
    latest === null
      ? { border: "1px solid var(--cui-secondary-color)" }
      : { background: inMarket ? "var(--sl-positive)" : "var(--cui-secondary-color)" };
  const label =
    latest === null
      ? t("feed.noSignalsYet")
      : inMarket
        ? t("feed.inMarket", { symbol: latest.symbol })
        : t("feed.standingOut");

  return (
    <span className="d-flex align-items-center gap-2 small text-body-secondary text-nowrap overflow-hidden">
      <span aria-hidden className="rounded-circle flex-shrink-0" style={{ width: 7, height: 7, ...dot }} />
      <span className="fw-semibold text-body">{label}</span>
      {latest !== null && <span>· {formatSignalTimeBrief(latest.receivedAt)}</span>}
    </span>
  );
}
